import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { FileText, Mail, Phone, ScrollText, ShieldCheck, X } from "lucide-react";
import { fileUrl } from "@/lib/upload";
import api from "@/lib/api";

export const hasPrivacy = (c) => !!(c?.privacy_text || c?.privacy_file_id);
export const hasRules = (c) => !!(c?.rules_text || c?.rules_file_id || c?.contact_email || c?.contact_phone || hasPrivacy(c));

export const PrivacyLink = ({ c, light = false, className = "" }) => {
  if (!hasPrivacy(c)) return null;
  return (
    <a href={`/privacy/${c.id}`} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-1.5 font-medium underline ${light ? "text-white" : "text-primary"} ${className}`} data-testid="contest-privacy-link">
      <ShieldCheck className="h-4 w-4" /> Informativa privacy
    </a>
  );
};

export const ContactLinks = ({ c, className = "", light = false }) => {
  if (!c?.contact_email && !c?.contact_phone) return null;
  const cls = `inline-flex items-center gap-1 underline ${light ? "text-white/80 hover:text-white" : "text-primary"}`;
  return (
    <div className={`flex flex-wrap gap-3 text-xs ${className}`} data-testid="contest-contacts">
      {c.contact_email && <a className={cls} href={`mailto:${c.contact_email}`} data-testid="contest-contact-email"><Mail className="h-3.5 w-3.5" /> {c.contact_email}</a>}
      {c.contact_phone && <a className={cls} href={`tel:${c.contact_phone.replace(/[^+0-9]/g, "")}`} data-testid="contest-contact-phone"><Phone className="h-3.5 w-3.5" /> {c.contact_phone}</a>}
    </div>
  );
};

export const RulesBlock = ({ c, light = false, compact = false, source = "concorsi" }) => {
  if (!hasRules(c)) return null;
  const muted = light ? "text-white/70" : "text-muted-foreground";
  const track = () => api.trackRulesView(c.id, source);
  return (
    <div className={`space-y-2 text-sm ${compact ? "text-xs" : ""}`} data-testid="contest-rules">
      {c.rules_file_id && (
        <a href={fileUrl(c.rules_file_id)} target="_blank" rel="noreferrer" onClick={track} className={`inline-flex items-center gap-1.5 font-medium underline ${light ? "text-white" : "text-primary"}`} data-testid="contest-rules-pdf">
          <FileText className="h-4 w-4" /> Regolamento (PDF)
        </a>
      )}
      {c.rules_text && (
        <details className={muted} data-testid="contest-rules-text" onToggle={(e) => e.currentTarget.open && track()}>
          <summary className="cursor-pointer inline-flex items-center gap-1.5"><ScrollText className="h-3.5 w-3.5" /> Regolamento completo</summary>
          <p className="mt-1 whitespace-pre-wrap">{c.rules_text}</p>
        </details>
      )}
      <PrivacyLink c={c} light={light} />
      <ContactLinks c={c} light={light} />
    </div>
  );
};

export const rulesUrl = (id, src) => `${window.location.origin}/regolamento/${id}${src ? `?src=${src}` : ""}`;

export const RulesQr = ({ c, size = 110, light = false, className = "", src = "totem" }) => {
  if (!c?.rules_text && !c?.rules_file_id) return null;
  return (
    <div className={`inline-flex items-center gap-3 rounded-2xl p-3 ${light ? "bg-white/95 text-slate-900" : "bg-card border border-border"} ${className}`} data-testid="rules-qr">
      <div className="rounded-lg bg-white p-1.5"><QRCodeSVG value={rulesUrl(c.id, src)} size={size} /></div>
      <div className="text-left"><p className="font-display font-bold leading-tight">Leggi il regolamento</p><p className={`text-xs ${light ? "text-slate-500" : "text-muted-foreground"}`}>Scansiona il QR con il telefono</p></div>
    </div>
  );
};

export const RulesButton = ({ c }) => {
  const [open, setOpen] = useState(false);
  if (!hasRules(c)) return null;
  return (
    <>
      <button data-testid="rules-open-button" onClick={() => { setOpen(true); api.trackRulesView(c.id, "totem_pannello"); }} className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white underline">
        <ScrollText className="h-3.5 w-3.5" /> Regolamento{hasPrivacy(c) ? ", privacy" : ""} e contatti
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={() => setOpen(false)} data-testid="rules-modal">
          <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl bg-slate-900 border border-white/10 p-6 text-white space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2"><ScrollText className="h-5 w-5 text-amber-400" /><h2 className="font-display font-bold text-lg flex-1">Regolamento{hasPrivacy(c) ? ", privacy" : ""} e contatti</h2><button onClick={() => setOpen(false)} data-testid="rules-close-button" className="text-white/60 hover:text-white"><X className="h-5 w-5" /></button></div>
            <p className="text-sm text-white/60">{c.name}</p>
            {c.rules_file_id && <a href={fileUrl(c.rules_file_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 font-medium underline" data-testid="contest-rules-pdf"><FileText className="h-4 w-4" /> Apri regolamento (PDF)</a>}
            {c.rules_text && <p className="text-sm whitespace-pre-wrap text-white/85" data-testid="contest-rules-text">{c.rules_text}</p>}
            <PrivacyLink c={c} light />
            <ContactLinks c={c} light />
          </div>
        </div>
      )}
    </>
  );
};
